"use client";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { ChevronDown, ChevronUp, Search } from "lucide-react";
import { FC, RefObject, useCallback, useRef, useState } from "react";
import { toast } from "sonner";

interface QuickSearchInputProps {
  onSearch: (searchValue: string) => number[];
  onNavigate: (index: number) => void;
  /**
   * 搜尋框的 ref，呼叫端要自己聚焦搜尋框時傳入（例：攔下 Ctrl+F／⌘F 改成聚焦這裡）
   */
  inputRef?: RefObject<HTMLInputElement | null>;
  /**
   * 搜尋框的提示文字；不傳時用通用的「請輸入關鍵字」
   */
  placeholder?: string;
}

export const QuickSearchInput: FC<QuickSearchInputProps> = ({
  onSearch,
  onNavigate,
  inputRef,
  placeholder,
}) => {
  // =========================================================
  // Hooks
  // =========================================================
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [results, setResults] = useState<number[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(-1);

  // =========================================================
  // Variables
  // =========================================================

  // =========================================================
  // Functions
  // =========================================================
  /**
   * 處理搜尋功能
   */
  const handleSearch = useCallback(() => {
    if (!searchInputRef.current) return;

    searchInputRef.current.value = searchInputRef.current?.value.trim() || "";

    if (!searchInputRef.current.value) {
      setResults([]);
      setCurrentIndex(-1);
      return;
    }

    const searchResults = onSearch(searchInputRef.current.value);
    setResults(searchResults);
    if (searchResults.length > 0) {
      setCurrentIndex(0);
      onNavigate(searchResults[0]);
    } else {
      setCurrentIndex(-1);
      toast.info("找不到相關資料");
    }
  }, [onSearch, onNavigate]);

  /**
   * 處理搜尋功能按鍵事件
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleSearch();
      }
    },
    [handleSearch]
  );

  /**
   * 處理上一個
   */
  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      onNavigate(results[newIndex]);
    }
  }, [currentIndex, results, onNavigate]);

  /**
   * 處理下一個
   */
  const handleNext = useCallback(() => {
    if (currentIndex < results.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      onNavigate(results[newIndex]);
    }
  }, [currentIndex, results, onNavigate]);

  // =========================================================
  // Effects
  // =========================================================

  // =========================================================
  // Render
  // =========================================================
  return (
    <div className="border-border flex flex-1 items-center rounded-full border">
      <Input
        ref={(element) => {
          searchInputRef.current = element;
          if (inputRef != null) {
            inputRef.current = element;
          }
        }}
        placeholder={placeholder ?? "請輸入關鍵字"}
        type="text"
        extra="flex-1 relative flex flex-col h-10"
        className="sm:w-50 w-full flex-1 rounded-l-full border-none"
        onKeyDown={handleKeyDown}
      />
      <div className="flex items-center">
        <div className="bg-on-background flex items-center self-stretch whitespace-nowrap border-r">
          {results.length > 0 ? (
            <span className="text-muted-foreground px-2 text-sm sm:px-4">
              {currentIndex + 1} / {results.length}
            </span>
          ) : (
            <Button
              type="button"
              variant="outline"
              className="rounded-none border-none"
              onClick={handleSearch}
            >
              <Search />
            </Button>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          className="rounded-none border-none"
          onClick={handlePrevious}
          disabled={currentIndex <= 0}
        >
          <ChevronUp />
        </Button>
        <Button
          type="button"
          variant="outline"
          className="rounded-l-none rounded-r-full border-none"
          onClick={handleNext}
          disabled={currentIndex >= results.length - 1}
        >
          <ChevronDown />
        </Button>
      </div>
    </div>
  );
};
